<script setup lang="ts">
import { useCourseRepository } from '@/repositories/courseRepo'

import { useAppStore } from '@/stores/AppStore'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

// components
import LoadingDialog from '@components/LoadingDialog.vue'
import Image from '@/components/Image.vue'
import { CourseLineSell } from '@/type/Course'
import { getCourseDisplayNamei18n } from '@/utils/course'
const courseDisplayName = (course: CourseLineSell) => getCourseDisplayNamei18n(course.liff_data, appStore.language)

enum OrderOption {
  rec = 'rec',
  asc = 'asc',
  desc = 'desc',
}

const appStore = useAppStore()
const router = useRouter()
const repository = useCourseRepository(appStore.clinicUUID)
const loading = ref(false) // first loading

const search = ref('')
const orderPrice = ref<OrderOption>(OrderOption.rec)
const course = ref<CourseLineSell[]>([])
const courseLoading = ref<boolean>(false)
const showSortOptions = ref(false)

const filteredCourse = computed(() => {
  course.value.sort((a, b) => {
    switch (orderPrice.value) {
      case OrderOption.rec:
        if (a.liff_data.display_order === undefined || b.liff_data.display_order === undefined) {
          return 0
        }

        return a.liff_data.display_order - b.liff_data.display_order // เลขน้อยแสดงก่อน
      case OrderOption.asc:
        return a.liff_data.cost_on_LINE - b.liff_data.cost_on_LINE
      case OrderOption.desc:
        return b.liff_data.cost_on_LINE - a.liff_data.cost_on_LINE
      default:
        return 0
    }
  })

  if (search.value) {
    return course.value.filter(course => {
      // ค้นหาข้ามทุกภาษา - ผู้ใช้ EN/CN เห็นชื่อที่แปลแล้ว แต่ก็อาจพิมพ์ชื่อไทยมาค้นได้
      const keyword = search.value.toLowerCase()
      const searchableNames = [
        course.liff_data.display_name,
        course.liff_data.display_name_en,
        course.liff_data.display_name_cn,
      ]
      if (
        searchableNames.some(name => (name ?? '').toLowerCase().includes(keyword)) ||
        course.liff_data.cost_on_LINE.toString().includes(search.value)
      ) {
        return course
      }
    })
  }

  return course.value
})

function toggleSortOptions(option: OrderOption) {
  showSortOptions.value = false
  orderPrice.value = option
}

const onClickCourse = (course_uuid: string) => {
  router.push({
    name: 'CourseSellDetail',
    params: { course_uuid: course_uuid },
  })
}

const onCheckout = () => {
  router.replace({ name: 'CourseCheckout' })
}

onMounted(async () => {
  loading.value = true
  // ถ้ายังไม่มี course ใน store ให้ fetch
  if (!appStore.sell_course?.length) {
    courseLoading.value = true
  } else {
    course.value = appStore.sell_course
  }

  const sell_course = await repository.fetchSellCourseList()

  // ถ้ามี uuid ของ patient = เป็น patient แล้ว มี quota แล้ว ให้ filter quota
  // ถ้ายังไม่มี ให้ถือว่า buy_count = 0
  course.value = sell_course
  appStore.setGlobalSellCourse(course.value)
  courseLoading.value = false
  loading.value = false
})
</script>

<template>
  <LoadingDialog v-if="loading || !appStore.clinic" :show="true" />

  <div v-else>
    <div class="bg-primary2">
      <van-row align="center" justify="space-around" class="search text-center">
        <van-col style="width: 100%; overflow: hidden; text-overflow: ellipsis" span="5">
          <img src="@/assets/aura_logo.png" alt="Aura Logo" style="width: 100%; height: 100%" />
        </van-col>

        <van-col span="4"></van-col>

        <van-col span="15" class="tw-flex tw-justify-end tw-items-center tw-gap-4">
          <van-badge :content="appStore.cart.length > 0 ? appStore.cart.length : undefined">
            <van-icon name="shopping-cart-o" size="25" @click="onCheckout" color="white" />
          </van-badge>
          <LanguageButton />
        </van-col>
      </van-row>
    </div>

    <div class="tw-flex tw-items-center tw-gap-3 tw-px-[15px] tw-pt-[15px]">
      <van-field
        v-model="search"
        name="search"
        :placeholder="$t('course.search_course')"
        left-icon="search"
        class="tw-flex-1 tw-border-2 tw-border-primary2 tw-rounded-[20px] tw-overflow-hidden"
      />

      <v-menu v-model="showSortOptions" offset-y>
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            color="grey-lighten-1"
            variant="flat"
            height="48"
            class="text-white font-weight-bold tw-shrink-0"
          >
            <span v-if="orderPrice === OrderOption.rec">{{ $t('course.sort_price.rec') }}</span>
            <span v-if="orderPrice === OrderOption.asc">{{ $t('course.sort_price.asc') }}</span>
            <span v-if="orderPrice === OrderOption.desc">{{ $t('course.sort_price.desc') }}</span>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="toggleSortOptions(OrderOption.rec)">
            <v-list-item-title>{{ $t('course.sort_price.rec') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="toggleSortOptions(OrderOption.asc)">
            <v-list-item-title>{{ $t('course.sort_price.asc') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="toggleSortOptions(OrderOption.desc)">
            <v-list-item-title>{{ $t('course.sort_price.desc') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div v-if="courseLoading">
      <van-loading style="margin-top: 20px" size="30px" color="#0094ff" vertical>Loading...</van-loading>
    </div>

    <div v-else-if="course.length <= 0">
      <h3 class="text-center" style="color: #999999">
        {{ $t('course.buy_course.no_course_list') }}
      </h3>
    </div>

    <!-- height: calc(100vh - 174px); -->
    <div v-else>
      <div class="tw-grid tw-grid-cols-2 tw-gap-5 tw-p-[10px]" style="height: calc(100dvh - 210px); overflow-y: auto">
        <div
          @click="onClickCourse(item.uuid + '')"
          v-for="item in filteredCourse"
          :key="item.uuid"
          class="tw-border-black-500 tw-w-full tw-cursor-pointer tw-rounded-2xl tw-border tw-p-0"
        >
          <div
            class="tw-aspect-square tw-w-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-t-2xl tw-bg-gray-100"
          >
            <Image :src="item.liff_data.images[0].url" alt="Course Image" />
          </div>

          <div class="tw-p-2">
            <div class="tw-text-left" style="font-weight: bold; margin-bottom: 5px">
              {{ courseDisplayName(item) }}
            </div>
            <div class="tw-text-left" style="color: #777777">{{ item.liff_data.cost_on_LINE.toLocaleString() }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-center {
  text-align: center;
}

.text-bold {
  font-weight: bold;
}

.search {
  padding: 15px;
}

.list-header {
  padding: 15px;
}

.margin-y {
  margin-top: var(--margin);
  margin-bottom: var(--margin);
}

.clinic_branch {
  font-weight: bold;
}

.active {
  color: green;
}

.inactive {
  color: #555555;
}
</style>
